import { useEffect, useState } from 'react'
import { apiFetch } from '../../lib/pfhApi'
import { PageShell, SectionList, Row, Note } from './SectionList'
import { apiErrorMessage } from '../../lib/apiError'

interface Member {
  id: number
  email: string
  name: string
}

export function HouseholdPage() {
  const [members, setMembers] = useState<Member[]>([])
  const [inviteEmail, setInviteEmail] = useState('')
  const [msg, setMsg] = useState<string | null>(null)
  const [sending, setSending] = useState(false)

  async function load() {
    const r = await apiFetch('/auth/household')
    if (r.ok) {
      const data = await r.json() as { members: Member[] }
      setMembers(data.members ?? [])
    }
  }

  useEffect(() => { void load() }, [])

  async function invite() {
    if (!inviteEmail.trim()) { setMsg('Enter an email address first.'); return }
    setMsg(null)
    setSending(true)
    try {
      const r = await apiFetch('/auth/household/join', {
        method: 'POST',
        body: JSON.stringify({ email: inviteEmail }),
      })
      if (r.ok) { setMsg(`Invite sent to ${inviteEmail}.`); setInviteEmail(''); void load() }
      else {
        const body = await r.json().catch(() => ({}))
        setMsg(apiErrorMessage(body, 'Could not send the invite. Please try again.'))
      }
    } catch {
      setMsg('Could not reach the server. Check your connection and try again.')
    } finally {
      setSending(false)
    }
  }

  return (
    <PageShell title="Household">
      <SectionList title="Members">
        {members.length === 0 && <Row label="No members" />}
        {members.map((m) => (
          <Row key={m.id} label={m.name || m.email} value={m.email} />
        ))}
      </SectionList>

      <SectionList title="Invite">
        <Row
          label="Email"
          trailing={
            <input
              type="email"
              inputMode="email"
              autoComplete="email"
              aria-label="Invitee email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="name@example.com"
              style={{
                background: 'var(--color-bg-primary)',
                color: 'var(--color-text-primary)',
                border: '1px solid var(--color-bg-border)',
                borderRadius: 8,
                padding: '6px 10px',
                minHeight: 44,
                fontSize: 15,
                width: 180,
              }}
            />
          }
        />
        <Row label={sending ? 'Sending…' : 'Send invite'} onClick={invite} disabled={sending} />
      </SectionList>
      {msg && <Note status>{msg}</Note>}
    </PageShell>
  )
}
